// Keep sources detached until the visible art-directed variant is needed.
// preload="none" alone does not prevent an autoplay video from downloading.
const videos = new Set();
const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
const nearby = new Set();
const blocked = new WeakSet();
const pending = new WeakSet();
const inactive = new WeakSet();

/**
 * Start only the visible, eligible variant; keep its poster on autoplay rejection.
 * @param {HTMLVideoElement} video background video
 */
function updateVideo(video) {
  const visible = video.getClientRects().length > 0 && getComputedStyle(video).visibility !== 'hidden';
  if (!visible || inactive.has(video) || reducedMotion.matches || document.hidden || blocked.has(video) || !nearby.has(video)) {
    video.pause();
    return;
  }
  if (!video.hasAttribute('src')) {
    video.src = video.dataset.src;
    video.load();
  }
  if (video.paused && !pending.has(video)) {
    pending.add(video);
    let interrupted = false;
    video.play().catch((error) => {
      // Low-power mode can reject autoplay. Do not hide the poster or retry forever.
      // A pause during an in-flight play instead causes AbortError and is retryable.
      interrupted = error.name === 'AbortError';
      if (!interrupted) blocked.add(video);
    }).finally(() => {
      pending.delete(video);
      if (interrupted || video.paused) updateVideo(video);
    });
  }
}

const observer = 'IntersectionObserver' in window ? new IntersectionObserver((entries) => {
  entries.forEach(({target, isIntersecting}) => {
    if (isIntersecting) nearby.add(target);
    else nearby.delete(target);
    updateVideo(target);
  });
}, {rootMargin: '300px'}) : null;

/**
 * Register a newly rendered subtree without observing every vendor DOM mutation.
 * Hydrating components dispatch backgroundvideos:register after insertion.
 * @param {Document|Element} root subtree containing background videos
 * @param {boolean} [active] explicit component visibility, including closing transitions
 */
function registerVideos(root, active) {
  const candidates = root.matches?.('video[data-bg-video]') ? [root] : root.querySelectorAll('video[data-bg-video]');
  candidates.forEach((video) => {
    if (typeof active === 'boolean') {
      if (active) inactive.delete(video);
      else inactive.add(video);
    }
    if (!videos.has(video)) {
      videos.add(video);
      if (observer) observer.observe(video);
      // Eager videos start immediately; unsupported browsers retain playback.
      if (!observer || video.dataset.bgVideo === 'eager') nearby.add(video);
    }
    updateVideo(video);
  });
}

document.addEventListener('backgroundvideos:register', (event) => registerVideos(event.target));
// Visibility-only panel changes need not cross an intersection threshold.
// Explicit state also prevents a closing transition from restarting playback.
document.addEventListener('backgroundvideos:visibility', (event) => {
  if (typeof event.detail?.visible === 'boolean') registerVideos(event.target, event.detail.visible);
});
registerVideos(document);

const updateVideos = () => videos.forEach(updateVideo);
window.addEventListener('resize', updateVideos, {passive: true});
document.addEventListener('visibilitychange', updateVideos);
reducedMotion.addEventListener('change', updateVideos);
